import { observer } from 'mobx-react-lite'
import { Folder, Trash } from 'iconoir-react'
import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/ui/page-header'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { parsePacketForms } from 'app/lib/forms/builder'
import type { PacketRow, UsePacketsListReturn } from './usePacketsList'
import { FORMS_SECTION_TABS } from './formsSectionTabs'

export const PacketsListPage = observer(function PacketsListPage({
  packets,
  creating,
  createPacket,
  openPacket,
  deletePacket,
}: UsePacketsListReturn) {
  const columns = useMemo<ColumnDef<PacketRow>[]>(
    () => [
      {
        id: `name`,
        accessorKey: `name`,
        header: `Name`,
        cell: (props) => <span className="font-medium">{props.getValue<string>()}</span>,
        meta: { defaultVisible: true },
      },
      {
        id: `forms`,
        accessorFn: (packet) => parsePacketForms(packet.formsJson).length,
        header: `Forms`,
        meta: { defaultVisible: true },
      },
      {
        id: `updated`,
        accessorKey: `updatedDate`,
        header: `Updated`,
        cell: (props) => moment(props.getValue<string>()).format(`MMM D, YYYY`),
        meta: { defaultVisible: true },
      },
      {
        id: `actions`,
        header: ``,
        meta: { defaultVisible: true },
        cell: (props) => (
          <TooltipWrapper message="Delete packet">
            <Button
              variant="icon"
              size="icon-sm"
              aria-label="Delete packet"
              onClick={(e) => {
                e.stopPropagation()
                deletePacket(props.row.original)
              }}
            >
              <Trash className="text-red-500" />
            </Button>
          </TooltipWrapper>
        ),
      },
    ],
    [deletePacket],
  )

  const newButton = (
    <Button variant="primary" size="sm" disabled={creating} onClick={createPacket}>
      New packet
    </Button>
  )

  return (
    <PageScroll variant="table" header={<PageHeader title="Forms" tabs={FORMS_SECTION_TABS} />}>
      <div className="h-full pt-6">
        <SimpleTable
          columns={columns}
          data={packets}
          hideFooter
          meta={{ onRowClick: openPacket }}
          emptyState={
            <TableEmptyState
              icon={<Folder className="h-6 w-6" strokeWidth={1.5} />}
              title="No packets yet"
              description="A packet is the set of forms a patient gets in one link, with your logo, colours, and a welcome message. Publish a form first, then add it here."
              action={newButton}
            />
          }
          edgePadded
          extraSlot={newButton}
        />
      </div>
    </PageScroll>
  )
})
